import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import { useAuth } from "../hooks/useAuth";
import { useAccountType } from "../hooks/useAccountType";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import "./training-schedule.css";

/**
 * Training Schedule - Google Calendar-style week view
 * Allows users to add meetings with title, description, time, color, and Zoom link
 */

// Utility functions
const startOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day; // Sunday is 0
  return new Date(d.setDate(diff));
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const formatTime = (date) => {
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Show hours from 7:30 AM to 11:00 PM (7.5 to 23)
const HOURS = Array.from({ length: 16 }, (_, i) => 7 + i); // 7 AM to 11 PM (7, 8, 9, ..., 22)
const DAYS_OF_WEEK = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

// Helper to format hour with half-hour support
const formatHourSlot = (hour, isHalfHour = false) => {
  const date = new Date(0, 0, 0, hour, isHalfHour ? 30 : 0);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const DEFAULT_COLORS = [
  { name: "Blue", value: "#4A90E2" },
  { name: "Orange", value: "#F5A623" },
  { name: "Green", value: "#7ED321" },
  { name: "Red", value: "#D0021B" },
  { name: "Purple", value: "#9013FE" },
  { name: "Pink", value: "#E91E63" },
  { name: "Teal", value: "#00BCD4" },
  { name: "Amber", value: "#FFC107" },
];

export default function TrainingSchedule() {
  const { user } = useAuth();
  const { plan: accountPlan } = useAccountType();
  const isNewAgent = (accountPlan || "New Agent") === "New Agent";
  const [currentWeekStart, setCurrentWeekStart] = useState(
    startOfWeek(new Date())
  );
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    startTime: formatDateForInput(new Date()),
    endTime: formatDateForInput(addDays(new Date(), 0)),
    color: DEFAULT_COLORS[0].value,
    zoomLink: "",
    isRecurring: false,
    recurrencePattern: "weekly",
    recurrenceEndDate: formatDateForInput(addDays(new Date(), 30)).split("T")[0],
    recurrenceCount: 4,
  });

  // Calculate week dates
  const weekDates = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  }, [currentWeekStart]);

  // New Agent visibility filter
  const isEventVisibleForPlan = (event) => {
    if (accountPlan !== "New Agent" && accountPlan !== "Agent") return true;
    const title = (event?.title || "").toLowerCase();
    if (title.includes("national call")) return true;
    if (title.includes("maxout") && (accountPlan !== "New Agent")) return true;
    if (title.includes("product training") && (accountPlan !== "New Agent")) return true;
    if (title.includes("abc")) return true; // covers ABC / ABCs variations
    if (title.includes("weekly training 29")) return true;
    return false;
  };

  // Fetch events from Firebase
  useEffect(() => {
    if (!user) return;

    const eventsRef = collection(db, "trainingSchedule");
    // Fetch all events (not just current week) to support recurring events
    const unsubscribe = onSnapshot(eventsRef, (snapshot) => {
      const fetchedEvents = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          startTime: data.startTime?.toDate?.() || new Date(data.startTime),
          endTime: data.endTime?.toDate?.() || new Date(data.endTime),
        };
      });
      
      // Filter to show only events in current week view
      const weekEnd = addDays(currentWeekStart, 7);
      const filteredEvents = fetchedEvents
        .filter((event) => event.startTime < weekEnd && event.endTime > currentWeekStart)
        .filter(isEventVisibleForPlan);
      
      setEvents(filteredEvents);
    });

    return () => unsubscribe();
  }, [user, currentWeekStart]);

  // Navigate weeks
  const goToPreviousWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, -7));
  };

  const goToNextWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, 7));
  };

  const goToToday = () => {
    setCurrentWeekStart(startOfWeek(new Date()));
  };

  // Open dialog for new event
  const openNewEventDialog = () => {
    const now = new Date();
    const nextHour = new Date(now);
    nextHour.setHours(now.getHours() + 1, 0, 0, 0);
    const endTime = new Date(nextHour);
    endTime.setHours(nextHour.getHours() + 1);

    setFormData({
      title: "",
      description: "",
      startTime: formatDateForInput(nextHour),
      endTime: formatDateForInput(endTime),
      color: DEFAULT_COLORS[0].value,
      zoomLink: "",
      isRecurring: false,
      recurrencePattern: "weekly",
      recurrenceEndDate: formatDateForInput(addDays(nextHour, 30)).split("T")[0],
      recurrenceCount: 4,
    });
    setEditingEvent(null);
    setDialogOpen(true);
  };

  // Open dialog to edit event
  const openEditEventDialog = (event) => {
    if (isNewAgent) {
      alert("Schedule editing is not available for your plan.");
      return;
    }
    setFormData({
      title: event.title,
      description: event.description || "",
      startTime: formatDateForInput(event.startTime),
      endTime: formatDateForInput(event.endTime),
      color: event.color,
      zoomLink: event.zoomLink || "",
      isRecurring: false, // User can make it recurring from edit mode
      recurrencePattern: event.recurrencePattern || "weekly",
      recurrenceEndDate: formatDateForInput(addDays(new Date(), 30)).split("T")[0],
      recurrenceCount: 4,
    });
    setEditingEvent(event);
    setDialogOpen(true);
  };

  // Generate recurring event dates
  const generateRecurringDates = (startDate, pattern, count) => {
    const dates = [];
    const start = new Date(startDate);
    
    for (let i = 0; i < count; i++) {
      // Create a new date for each occurrence, preserving the time
      const newDate = new Date(start.getTime()); // Clone with exact time
      
      switch (pattern) {
        case "daily":
          newDate.setDate(newDate.getDate() + i);
          break;
        case "weekly":
          newDate.setDate(newDate.getDate() + (i * 7));
          break;
        case "biweekly":
          newDate.setDate(newDate.getDate() + (i * 14));
          break;
        case "monthly":
          newDate.setMonth(newDate.getMonth() + i);
          break;
        default:
          break;
      }
      
      dates.push(newDate);
    }
    
    return dates;
  };

  // Save event
  const handleSaveEvent = async (e) => {
    e.preventDefault();

    if (!user) return;
    if (isNewAgent) {
      alert("Schedule editing is not available for your plan.");
      return;
    }

    const startTime = new Date(formData.startTime);
    const endTime = new Date(formData.endTime);
    const duration = endTime - startTime;

    try {
      if (editingEvent) {
        // Check if user wants to convert to recurring series
        if (formData.isRecurring) {
          // Create recurring series starting from the edited event's time
          const recurringDates = generateRecurringDates(
            startTime,
            formData.recurrencePattern,
            formData.recurrenceCount
          );

          console.log(`Converting to ${recurringDates.length} recurring events:`, recurringDates);

          // Update the original event
          await updateDoc(doc(db, "trainingSchedule", editingEvent.id), {
            title: formData.title,
            description: formData.description,
            startTime: recurringDates[0],
            endTime: new Date(recurringDates[0].getTime() + duration),
            color: formData.color,
            zoomLink: formData.zoomLink,
            userId: user.uid,
            userEmail: user.email,
            isRecurring: true,
            recurrencePattern: formData.recurrencePattern,
            updatedAt: serverTimestamp(),
          });

          // Create additional recurring events (skip first one as it's the updated original)
          if (recurringDates.length > 1) {
            const additionalBatch = recurringDates.slice(1).map(async (date) => {
              const eventStartTime = new Date(date);
              const eventEndTime = new Date(date.getTime() + duration);

              console.log(`Creating additional recurring event for: ${eventStartTime.toLocaleString()}`);

              return addDoc(collection(db, "trainingSchedule"), {
                title: formData.title,
                description: formData.description,
                startTime: eventStartTime,
                endTime: eventEndTime,
                color: formData.color,
                zoomLink: formData.zoomLink,
                userId: user.uid,
                userEmail: user.email,
                isRecurring: true,
                recurrencePattern: formData.recurrencePattern,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
            });

            await Promise.all(additionalBatch);
          }

          alert(`✅ Successfully converted to ${recurringDates.length} recurring ${formData.recurrencePattern} meetings!\n\nNavigate through the weeks to see all occurrences.`);
        } else {
          // Regular update without recurring
          const eventData = {
            title: formData.title,
            description: formData.description,
            startTime: startTime,
            endTime: endTime,
            color: formData.color,
            zoomLink: formData.zoomLink,
            userId: user.uid,
            userEmail: user.email,
            updatedAt: serverTimestamp(),
          };
          await updateDoc(doc(db, "trainingSchedule", editingEvent.id), eventData);
        }
      } else {
        // Create new event(s)
        if (formData.isRecurring) {
          // Create multiple recurring events
          const recurringDates = generateRecurringDates(
            startTime,
            formData.recurrencePattern,
            formData.recurrenceCount
          );

          console.log(`Creating ${recurringDates.length} recurring events:`, recurringDates);

          const batch = recurringDates.map(async (date) => {
            const eventStartTime = new Date(date);
            const eventEndTime = new Date(date.getTime() + duration);

            console.log(`Creating event for: ${eventStartTime.toLocaleString()}`);

            return addDoc(collection(db, "trainingSchedule"), {
              title: formData.title,
              description: formData.description,
              startTime: eventStartTime,
              endTime: eventEndTime,
              color: formData.color,
              zoomLink: formData.zoomLink,
              userId: user.uid,
              userEmail: user.email,
              isRecurring: true,
              recurrencePattern: formData.recurrencePattern,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
          });

          await Promise.all(batch);
          
          alert(`✅ Successfully created ${recurringDates.length} recurring ${formData.recurrencePattern} meetings!\n\nNavigate through the weeks to see all occurrences.`);
        } else {
          // Create single event
          await addDoc(collection(db, "trainingSchedule"), {
            title: formData.title,
            description: formData.description,
            startTime: startTime,
            endTime: endTime,
            color: formData.color,
            zoomLink: formData.zoomLink,
            userId: user.uid,
            userEmail: user.email,
            isRecurring: false,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }
      }
      setDialogOpen(false);
    } catch (error) {
      console.error("Error saving event:", error);
      alert("Failed to save event. Please try again.");
    }
  };

  // Delete event
  const handleDeleteEvent = async () => {
    if (!editingEvent) return;

    if (isNewAgent) {
      alert("Schedule editing is not available for your plan.");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete "${editingEvent.title}"?`
      )
    )
      return;

    try {
      await deleteDoc(doc(db, "trainingSchedule", editingEvent.id));
      setDialogOpen(false);
    } catch (error) {
      console.error("Error deleting event:", error);
      alert("Failed to delete event. Please try again.");
    }
  };

  // Calculate event position in grid (7 AM to 11 PM view)
  const getEventStyle = (event, dayStart) => {
    const viewStartHour = 7; // 7 AM
    const viewEndHour = 23; // 11 PM
    const totalViewMinutes = (viewEndHour - viewStartHour) * 60; // Minutes in view
    
    // Set the view start to 7 AM on the same day
    const viewStart = new Date(dayStart);
    viewStart.setHours(viewStartHour, 0, 0, 0);
    
    const viewEnd = new Date(dayStart);
    viewEnd.setHours(viewEndHour, 0, 0, 0);
    
    const viewStartMs = viewStart.getTime();
    const viewEndMs = viewEnd.getTime();
    
    // Clamp event to visible time range
    const startMs = Math.max(event.startTime.getTime(), viewStartMs);
    const endMs = Math.min(event.endTime.getTime(), viewEndMs);
    
    // Calculate position relative to 7 AM
    const minutesFromTop = (startMs - viewStartMs) / (1000 * 60);
    const durationMinutes = (endMs - startMs) / (1000 * 60);
    
    const topPercent = (minutesFromTop / totalViewMinutes) * 100;
    const heightPercent = (durationMinutes / totalViewMinutes) * 100;
    
    // Ensure minimum height for visibility
    const minHeightPercent = 3.5;
    
    // Only show events that are within the visible time range
    if (event.endTime < viewStart || event.startTime > viewEnd) {
      return { display: 'none' };
    }
    
    return {
      top: `${topPercent}%`,
      height: `${Math.max(heightPercent, minHeightPercent)}%`,
      backgroundColor: event.color,
    };
  };

  // Filter events by day
  const getEventsForDay = (date) => {
    const dayStart = new Date(date.setHours(0, 0, 0, 0));
    const dayEnd = new Date(date.setHours(23, 59, 59, 999));
    
    return events.filter(
      (event) =>
        event.startTime < dayEnd && event.endTime > dayStart
    );
  };

  // Get timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div className="training-schedule">
      {/* Header */}
      <div className="schedule-header">
        <div className="header-left">
          <h1 className="schedule-title">Training Schedule</h1>
          <div className="timezone-info">Timezone: {timezone}</div>
        </div>
        <div className="header-right">
          <Button onClick={openNewEventDialog} className="add-event-btn">
            + Add Meeting
          </Button>
        </div>
      </div>

      {/* Week Navigation */}
      <div className="week-nav">
        <Button variant="outline" onClick={goToPreviousWeek}>
          ← Previous Week
        </Button>
        <Button variant="outline" onClick={goToToday}>
          Today
        </Button>
        <Button variant="outline" onClick={goToNextWeek}>
          Next Week →
        </Button>
        <div className="week-range">
          {weekDates[0].toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          })}{" "}
          -{" "}
          {weekDates[6].toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid-container">
        <div className="calendar-grid">
          {/* Header Row */}
          <div className="grid-header">
            <div className="time-column-header">Time</div>
            {weekDates.map((date, index) => (
              <div key={index} className="day-header">
                <div className="day-name">{DAYS_OF_WEEK[index]}</div>
                <div
                  className={`day-number ${
                    date.toDateString() === new Date().toDateString()
                      ? "today"
                      : ""
                  }`}
                >
                  {date.getDate()}
                </div>
              </div>
            ))}
          </div>

          {/* Time Grid */}
          <div className="grid-body">
            {/* Time Column */}
            <div className="time-column">
              {HOURS.map((hour) => (
                <div key={hour} className="time-slot">
                  {formatHourSlot(hour)}
                </div>
              ))}
            </div>

            {/* Day Columns */}
            {weekDates.map((date, dayIndex) => (
              <div key={dayIndex} className="day-column">
                {/* Hour Grid Background */}
                {HOURS.map((hour) => (
                  <div key={hour} className="hour-cell" />
                ))}

                {/* Events */}
                <div className="events-container">
                  {getEventsForDay(new Date(date)).map((event) => (
                    <div
                      key={event.id}
                      className="event-block event-block-fancy"
                      style={getEventStyle(event, new Date(date.setHours(0, 0, 0, 0)))}
                      onClick={(e) => {
                        if (isNewAgent) return;
                        // Don't trigger edit if clicking the join button
                        if (!e.target.closest('.event-join-btn')) {
                          openEditEventDialog(event);
                        }
                      }}
                      title={`${event.title}\n${formatTime(event.startTime)} - ${formatTime(event.endTime)}${event.isRecurring ? `\n🔁 Recurring ${event.recurrencePattern}` : ''}${event.zoomLink ? '\n📹 Zoom available' : ''}`}
                    >
                      <div className="event-card-content">
                        <div className="event-title-fancy">
                          {event.isRecurring && <span className="recurring-badge">{event.recurrencePattern === 'weekly' ? '🔁 Weekly' : event.recurrencePattern === 'monthly' ? '🔁 Monthly' : event.recurrencePattern === 'daily' ? '🔁 Daily' : event.recurrencePattern === 'biweekly' ? '🔁 Bi-weekly' : '🔁'}</span>}
                          <span className="event-title-text">{event.title}</span>
                        </div>
                        <div className="event-time-fancy">
                          <span className="time-icon">🕐</span>
                          <span>{formatTime(event.startTime)} - {formatTime(event.endTime)}</span>
                        </div>
                        {event.description && (
                          <div className="event-description-fancy">
                            {event.description.length > 50 ? event.description.substring(0, 50) + '...' : event.description}
                          </div>
                        )}
                        {event.zoomLink && (
                          <a
                            href={event.zoomLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="event-join-btn event-join-btn-fancy"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="join-icon">📹</span>
                            <span className="join-text">Join Zoom</span>
                          </a>
                        )}
                      </div>
                      <div className="event-card-shine"></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Event Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="event-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingEvent ? "Edit Meeting" : "Add New Meeting"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSaveEvent}>
            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                id="title"
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                required
                placeholder="Meeting title"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Meeting description"
                rows={3}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startTime">Start Time *</label>
                <input
                  id="startTime"
                  type="datetime-local"
                  value={formData.startTime}
                  onChange={(e) =>
                    setFormData({ ...formData, startTime: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="endTime">End Time *</label>
                <input
                  id="endTime"
                  type="datetime-local"
                  value={formData.endTime}
                  onChange={(e) =>
                    setFormData({ ...formData, endTime: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Color</label>
              <div className="color-picker">
                {DEFAULT_COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`color-option ${
                      formData.color === color.value ? "selected" : ""
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() =>
                      setFormData({ ...formData, color: color.value })
                    }
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="zoomLink">Zoom Meeting Link (Optional)</label>
              <input
                id="zoomLink"
                type="url"
                value={formData.zoomLink}
                onChange={(e) =>
                  setFormData({ ...formData, zoomLink: e.target.value })
                }
                placeholder="https://zoom.us/j/..."
              />
              {formData.zoomLink && (
                <a
                  href={formData.zoomLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="zoom-link-preview"
                >
                  Open Zoom Link
                </a>
              )}
            </div>

            <div className="form-group recurring-toggle">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isRecurring}
                  onChange={(e) =>
                    setFormData({ ...formData, isRecurring: e.target.checked })
                  }
                />
                <span>
                  {editingEvent 
                    ? "Convert to recurring meeting series" 
                    : "Make this a recurring meeting"}
                </span>
              </label>
            </div>

            {formData.isRecurring && (
              <div className="recurring-options">
                <div className="form-group">
                  <label htmlFor="recurrencePattern">Repeat</label>
                  <select
                    id="recurrencePattern"
                    value={formData.recurrencePattern}
                    onChange={(e) =>
                      setFormData({ ...formData, recurrencePattern: e.target.value })
                    }
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-weekly (Every 2 weeks)</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="recurrenceCount">Number of Occurrences</label>
                  <input
                    id="recurrenceCount"
                    type="number"
                    min="1"
                    max="52"
                    value={formData.recurrenceCount}
                    onChange={(e) =>
                      setFormData({ ...formData, recurrenceCount: parseInt(e.target.value) })
                    }
                  />
                  <div className="recurrence-preview">
                    {editingEvent && (
                      <div style={{ marginBottom: '8px', color: '#f5a623', fontWeight: '500' }}>
                        ⚠️ This will update the current event and create {formData.recurrenceCount - 1} additional occurrences
                      </div>
                    )}
                    {formData.recurrencePattern === "daily" && `Will create ${formData.recurrenceCount} daily meetings`}
                    {formData.recurrencePattern === "weekly" && `Will create ${formData.recurrenceCount} weekly meetings (${Math.ceil(formData.recurrenceCount / 4)} months)`}
                    {formData.recurrencePattern === "biweekly" && `Will create ${formData.recurrenceCount} bi-weekly meetings (${Math.ceil(formData.recurrenceCount / 2)} months)`}
                    {formData.recurrencePattern === "monthly" && `Will create ${formData.recurrenceCount} monthly meetings`}
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              {editingEvent && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDeleteEvent}
                >
                  Delete
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingEvent ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

